import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { useAuthStore } from '@/state/authStore';

interface UserSettings {
  email_notifications_enabled: boolean;
  pdf_password_configured: boolean;
}

const PDF_PASSWORD_RULE = '8文字以上で、大文字・小文字・数字・記号をそれぞれ1文字以上含めてください。';

export function UserSettingsPage() {
  const currentUser = useAuthStore((state) => state.currentUser);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [pdfPassword, setPdfPassword] = useState('');
  const [pdfPasswordConfirm, setPdfPasswordConfirm] = useState('');
  const [pdfEditing, setPdfEditing] = useState(false);
  const [pdfSaving, setPdfSaving] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfSaved, setPdfSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<UserSettings>('/me/settings')
      .then((resp) => {
        if (cancelled) return;
        setSettings(resp.data);
        setError(null);
      })
      .catch((e: { userMessage?: string }) => {
        if (cancelled) return;
        setError(e.userMessage ?? '設定を取得できませんでした');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateEmailNotifications = async (enabled: boolean) => {
    if (!settings) return;
    const previous = settings;
    setSettings({ ...settings, email_notifications_enabled: enabled });
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const resp = await api.patch<UserSettings>('/me/settings', {
        email_notifications_enabled: enabled,
      });
      setSettings(resp.data);
      setSaved(true);
    } catch (e: unknown) {
      setSettings(previous);
      const maybeError = e as { userMessage?: string };
      setError(maybeError.userMessage ?? '設定を保存できませんでした');
    } finally {
      setSaving(false);
    }
  };

  const updatePdfPassword = async () => {
    if (!settings) return;
    setPdfError(null);
    setPdfSaved(false);
    if (pdfPassword !== pdfPasswordConfirm) {
      setPdfError('確認用パスワードが一致しません');
      return;
    }
    setPdfSaving(true);
    try {
      const resp = await api.put<UserSettings>('/me/settings/pdf-password', {
        password: pdfPassword,
      });
      setSettings(resp.data);
      setPdfPassword('');
      setPdfPasswordConfirm('');
      setPdfEditing(false);
      setPdfSaved(true);
    } catch (e: unknown) {
      const maybeError = e as { userMessage?: string };
      setPdfError(maybeError.userMessage ?? 'PDFパスワードを保存できませんでした');
    } finally {
      setPdfSaving(false);
    }
  };

  const deletePdfPassword = async () => {
    if (!settings || !window.confirm('PDFパスワードを解除します。よろしいですか?')) return;
    setPdfSaving(true);
    setPdfError(null);
    setPdfSaved(false);
    try {
      const resp = await api.delete<UserSettings>('/me/settings/pdf-password');
      setSettings(resp.data);
      setPdfPassword('');
      setPdfPasswordConfirm('');
      setPdfEditing(false);
      setPdfSaved(true);
    } catch (e: unknown) {
      const maybeError = e as { userMessage?: string };
      setPdfError(maybeError.userMessage ?? 'PDFパスワードを解除できませんでした');
    } finally {
      setPdfSaving(false);
    }
  };

  const cancelPdfPasswordEdit = () => {
    setPdfPassword('');
    setPdfPasswordConfirm('');
    setPdfError(null);
    setPdfSaved(false);
    setPdfEditing(false);
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-semibold text-gray-900">ユーザー設定</h1>

      <div className="mt-6 rounded-lg bg-white p-5 shadow-sm">
        {loading ? (
          <p className="text-sm text-gray-500">読み込み中...</p>
        ) : settings ? (
          <div className="flex items-start justify-between gap-6">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">メール通知</h2>
              <p className="mt-1 text-sm text-gray-500">
                フィードバック依頼とコーチ確認依頼の通知を受け取ります。
              </p>
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
              {saved && !error && <p className="mt-3 text-sm text-green-700">保存しました</p>}
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={settings.email_notifications_enabled}
                disabled={saving}
                onChange={(e) => updateEmailNotifications(e.target.checked)}
                aria-label="メール通知"
              />
              <span className="h-6 w-11 rounded-full bg-gray-200 transition peer-checked:bg-blue-600 peer-focus:outline peer-focus:outline-2 peer-focus:outline-offset-2 peer-focus:outline-blue-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-60" />
              <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
            </label>
          </div>
        ) : (
          <p className="text-sm text-red-600">{error ?? '設定を取得できませんでした'}</p>
        )}
      </div>

      {!currentUser?.is_admin && settings && (
        <div className="mt-6 rounded-lg bg-white p-5 shadow-sm">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">PDFパスワード</h2>
            <p className="mt-1 text-sm text-gray-500">
              全体分析PDFをダウンロードした後、PDFを開く際に使用するパスワードです。
              パスワードを設定していない場合はPDFを閲覧できますが、ダウンロードはできません。
            </p>
          </div>

          <div
            className={`mt-4 rounded-md border p-4 ${
              settings.pdf_password_configured
                ? 'border-green-200 bg-green-50'
                : 'border-amber-200 bg-amber-50'
            }`}
          >
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${
                  settings.pdf_password_configured ? 'bg-green-600' : 'bg-amber-500'
                }`}
              >
                {settings.pdf_password_configured ? '✓' : '!'}
              </span>
              <div>
                <p
                  className={`text-sm font-semibold ${
                    settings.pdf_password_configured ? 'text-green-800' : 'text-amber-800'
                  }`}
                >
                  {settings.pdf_password_configured
                    ? 'PDFパスワードは設定済みです'
                    : 'PDFパスワードは未設定です'}
                </p>
                <p
                  className={`mt-1 text-sm ${
                    settings.pdf_password_configured ? 'text-green-700' : 'text-amber-700'
                  }`}
                >
                  {settings.pdf_password_configured
                    ? 'ダウンロードしたPDFは、設定済みのパスワードで開けます。'
                    : 'PDFをダウンロードするには、パスワードを設定してください。'}
                </p>
              </div>
            </div>
          </div>

          {pdfError && <p className="mt-3 text-sm text-red-600">{pdfError}</p>}
          {pdfSaved && !pdfError && <p className="mt-3 text-sm text-green-700">保存しました</p>}

          {!settings.pdf_password_configured || pdfEditing ? (
            <div className="mt-4 max-w-lg">
              <p className="text-xs text-gray-500">{PDF_PASSWORD_RULE}</p>
              <div className="mt-3 grid gap-3">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">
                    {settings.pdf_password_configured ? '新しいPDFパスワード' : 'PDFパスワード'}
                  </span>
                  <input
                    type="password"
                    value={pdfPassword}
                    onChange={(event) => setPdfPassword(event.target.value)}
                    autoComplete="new-password"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">
                    {settings.pdf_password_configured
                      ? '新しいPDFパスワード（確認）'
                      : 'PDFパスワード（確認）'}
                  </span>
                  <input
                    type="password"
                    value={pdfPasswordConfirm}
                    onChange={(event) => setPdfPasswordConfirm(event.target.value)}
                    autoComplete="new-password"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </label>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={updatePdfPassword}
                  disabled={pdfSaving || !pdfPassword || !pdfPasswordConfirm}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {pdfSaving
                    ? '保存中...'
                    : settings.pdf_password_configured
                      ? '変更を保存'
                      : 'パスワードを設定'}
                </button>
                {settings.pdf_password_configured && (
                  <button
                    type="button"
                    onClick={cancelPdfPasswordEdit}
                    disabled={pdfSaving}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    変更をやめる
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setPdfError(null);
                  setPdfSaved(false);
                  setPdfEditing(true);
                }}
                disabled={pdfSaving}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                パスワードを変更
              </button>
              <button
                type="button"
                onClick={deletePdfPassword}
                disabled={pdfSaving}
                className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                パスワードを解除
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
